import { supabase } from './supabaseService';
import type { BullhornSyncEntityType } from './bullhornSyncQueries';
import type { SyncChangeRow } from './bullhornSyncStore';

/** Where the record exists relative to the other system. */
export type SyncRecordOrigin = 'new_in_bullhorn' | 'updated_both' | 'new_in_werftools';

export const SYNC_ORIGIN_LABELS: Record<SyncRecordOrigin, string> = {
  new_in_bullhorn: 'Nieuw in Bullhorn',
  updated_both: 'Wijziging (beide systemen)',
  new_in_werftools: 'Alleen in Werftools',
};

export const SYNC_ORIGIN_HINTS: Record<SyncRecordOrigin, string> = {
  new_in_bullhorn: 'Staat in Bullhorn, nog niet (of niet gekoppeld) in Werftools — wordt geïmporteerd.',
  updated_both: 'Bestaat al in beide systemen — Bullhorn-data wordt bijgewerkt in Werftools.',
  new_in_werftools: 'Staat in Werftools zonder Bullhorn-koppeling — nog niet in Bullhorn.',
};

export function getChangeOrigin(change: Pick<SyncChangeRow, 'metadata' | 'bullhorn_id' | 'action'>): SyncRecordOrigin {
  const o = change.metadata?.origin;
  if (o === 'new_in_bullhorn' || o === 'new_in_werftools' || o === 'updated_both') return o;
  if (String(change.bullhorn_id || '').startsWith('local:')) return 'new_in_werftools';
  if (change.action === 'create') return 'new_in_bullhorn';
  if (change.action === 'update') return 'updated_both';
  return 'updated_both';
}

function bhIdFromCandidateRow(row: { ai_parsed_data?: unknown }): string | null {
  const parsed = row.ai_parsed_data as { id?: unknown } | null | undefined;
  if (parsed?.id == null || parsed.id === '') return null;
  return String(parsed.id).trim();
}

/** Bullhorn IDs already linked in Werftools for this tenant. */
export async function loadLocalBullhornIdSet(
  clientId: string,
  entity: BullhornSyncEntityType
): Promise<Set<string>> {
  const ids = new Set<string>();
  try {
    switch (entity) {
      case 'crm_company': {
        const { data } = await supabase
          .from('clients')
          .select('bullhorn_id')
          .or(`parent_client_id.eq.${clientId},id.eq.${clientId}`)
          .not('bullhorn_id', 'is', null);
        for (const r of data || []) {
          const id = String(r.bullhorn_id ?? '').trim();
          if (id) ids.add(id);
        }
        break;
      }
      case 'crm_contact': {
        const { data: companies } = await supabase
          .from('clients')
          .select('id')
          .or(`parent_client_id.eq.${clientId},id.eq.${clientId}`);
        const companyIds = (companies || []).map((c) => c.id);
        if (companyIds.length === 0) break;
        const { data } = await supabase
          .from('client_contacts')
          .select('bullhorn_id')
          .in('linked_client_id', companyIds)
          .not('bullhorn_id', 'is', null);
        for (const r of data || []) {
          const id = String(r.bullhorn_id ?? '').trim();
          if (id) ids.add(id);
        }
        break;
      }
      case 'vacancy': {
        const { data } = await supabase
          .from('vacancies')
          .select('bullhorn_id')
          .eq('client_id', clientId)
          .not('bullhorn_id', 'is', null);
        for (const r of data || []) {
          const id = String(r.bullhorn_id ?? '').trim();
          if (id) ids.add(id);
        }
        break;
      }
      case 'candidate': {
        const { data } = await supabase
          .from('candidates')
          .select('ai_parsed_data')
          .eq('client_id', clientId);
        for (const r of data || []) {
          const id = bhIdFromCandidateRow(r);
          if (id) ids.add(id);
        }
        break;
      }
      case 'placement': {
        const { data } = await supabase
          .from('placements')
          .select('data')
          .eq('client_id', clientId);
        for (const r of data || []) {
          const d = (r.data as { bullhorn_id?: unknown; bullhornId?: unknown }) || {};
          const id = String(d.bullhorn_id ?? d.bullhornId ?? '').trim();
          if (id) ids.add(id);
        }
        break;
      }
      default:
        break;
    }
  } catch (e) {
    console.warn('[bullhornSyncDiff] loadLocalBullhornIdSet failed', entity, e);
  }
  return ids;
}

export interface LocalOnlyRecord {
  localId: string;
  displayName: string;
}

/** Werftools records without a Bullhorn link (full-sync overview). */
export async function loadLocalOnlyRecords(
  clientId: string,
  entity: BullhornSyncEntityType,
  bhIdsSeenInRun: Set<string>
): Promise<LocalOnlyRecord[]> {
  const out: LocalOnlyRecord[] = [];
  try {
    switch (entity) {
      case 'crm_company': {
        const { data } = await supabase
          .from('clients')
          .select('id, name, bullhorn_id')
          .eq('parent_client_id', clientId);
        for (const r of data || []) {
          const bh = String(r.bullhorn_id ?? '').trim();
          if (!bh) out.push({ localId: r.id, displayName: String(r.name || 'Bedrijf') });
        }
        break;
      }
      case 'crm_contact': {
        const { data: companies } = await supabase
          .from('clients')
          .select('id')
          .or(`parent_client_id.eq.${clientId},id.eq.${clientId}`);
        const companyIds = (companies || []).map((c) => c.id);
        if (companyIds.length === 0) break;
        const { data } = await supabase
          .from('client_contacts')
          .select('id, first_name, last_name, email, bullhorn_id')
          .in('linked_client_id', companyIds);
        for (const r of data || []) {
          const bh = String(r.bullhorn_id ?? '').trim();
          if (!bh) {
            const name = [r.first_name, r.last_name].filter(Boolean).join(' ') || String(r.email || 'Contact');
            out.push({ localId: r.id, displayName: name });
          }
        }
        break;
      }
      case 'vacancy': {
        const { data } = await supabase
          .from('vacancies')
          .select('id, title, bullhorn_id')
          .eq('client_id', clientId);
        for (const r of data || []) {
          const bh = String(r.bullhorn_id ?? '').trim();
          if (!bh) out.push({ localId: r.id, displayName: String(r.title || 'Vacature') });
        }
        break;
      }
      case 'candidate': {
        const { data } = await supabase
          .from('candidates')
          .select('id, name, email, ai_parsed_data')
          .eq('client_id', clientId);
        for (const r of data || []) {
          const bh = bhIdFromCandidateRow(r);
          if (!bh) out.push({ localId: r.id, displayName: String(r.name || r.email || 'Kandidaat') });
        }
        break;
      }
      default:
        break;
    }
  } catch (e) {
    console.warn('[bullhornSyncDiff] loadLocalOnlyRecords failed', entity, e);
  }
  return out;
}

export function classifyBullhornRecord(
  bhId: string,
  localIds: Set<string>
): { origin: SyncRecordOrigin; action: 'create' | 'update' } {
  if (localIds.has(bhId)) {
    return { origin: 'updated_both', action: 'update' };
  }
  return { origin: 'new_in_bullhorn', action: 'create' };
}
